import { Component } from '@theme/component';

/**
 * Enhances native media links with thumbnail navigation, keyboard arrows and touch swipe.
 * @extends {Component<{images: HTMLElement[], thumbnails: HTMLAnchorElement[], slides: HTMLDivElement, strip: HTMLElement, previous?: HTMLButtonElement, next?: HTMLButtonElement}>}
 */
class EvenmornGallery extends Component {
  #initialized = false;
  #events;
  #touch;

  connectedCallback() {
    super.connectedCallback();
    // Preserve a native thumbnail selection made before this module finished loading.
    const linkedImage = this.#initialized
      ? undefined
      : this.refs.images?.findIndex((image) => image.id === window.location.hash.slice(1));
    this.#showCurrent(linkedImage === -1 ? undefined : linkedImage);
    this.#initialized = true;
    this.#events?.abort();
    this.#events = new AbortController();
    window.addEventListener('hashchange', this.#restoreLinkedImage, { signal: this.#events.signal });
    this.addEventListener('keydown', this.#keyboard, { signal: this.#events.signal });
    this.refs.slides?.addEventListener('touchstart', this.#startSwipe, { passive: true, signal: this.#events.signal });
    this.refs.slides?.addEventListener('touchend', this.#endSwipe, { passive: true, signal: this.#events.signal });
    this.setAttribute('data-enhanced', '');
  }

  updatedCallback() {
    super.updatedCallback();
    this.#showCurrent();
    this.#events?.abort();
    this.#events = new AbortController();
    window.addEventListener('hashchange', this.#restoreLinkedImage, { signal: this.#events.signal });
    this.addEventListener('keydown', this.#keyboard, { signal: this.#events.signal });
    this.refs.slides?.addEventListener('touchstart', this.#startSwipe, { passive: true, signal: this.#events.signal });
    this.refs.slides?.addEventListener('touchend', this.#endSwipe, { passive: true, signal: this.#events.signal });
  }

  disconnectedCallback() {
    this.#events?.abort();
    this.#touch = undefined;
    for (const image of this.refs.images || []) this.#pauseMedia(image);
    super.disconnectedCallback();
  }

  /** @param {HTMLElement} image - Gallery slide whose owned media must stop. */
  #pauseMedia(image) {
    for (const media of image.querySelectorAll('deferred-media, product-model')) {
      if ('pauseMedia' in media && typeof media.pauseMedia === 'function') media.pauseMedia();
    }
    for (const video of image.querySelectorAll('video')) video.pause();
  }

  #restoreLinkedImage = () => {
    const index = this.refs.images?.findIndex((image) => image.id === window.location.hash.slice(1));
    if (index !== undefined && index >= 0) this.#showCurrent(index);
  };

  #startSwipe = (event) => { const touch = event.touches[0]; this.#touch = touch && { x: touch.clientX, y: touch.clientY }; };
  #endSwipe = (event) => {
    const touch = event.changedTouches[0];
    if (!touch || !this.#touch) return;
    const x = touch.clientX - this.#touch.x;
    const y = touch.clientY - this.#touch.y;
    this.#touch = undefined;
    if (Math.abs(x) < 40 || Math.abs(x) < Math.abs(y)) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    this.#move((x < 0) !== rtl ? 1 : -1);
  };

  #keyboard = (event) => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('.ev-gallery__thumb');
    if (!link) return;
    const rtl = getComputedStyle(this).direction === 'rtl';
    if (event.key === 'ArrowRight') this.#move(rtl ? -1 : 1);
    else if (event.key === 'ArrowLeft') this.#move(rtl ? 1 : -1);
    else if (event.key === 'Home') this.#showCurrent(0);
    else if (event.key === 'End') this.#showCurrent((this.refs.images?.length || 1) - 1);
    else return;
    event.preventDefault();
    this.refs.thumbnails?.find((item) => item.getAttribute('aria-current') === 'true')?.focus({ preventScroll: true });
  };

  #move(delta) {
    const count = this.refs.images?.length || 0;
    if (!count) return;
    const index = this.refs.thumbnails.findIndex((link) => link.getAttribute('aria-current') === 'true');
    this.#showCurrent(Math.max(0, Math.min(count - 1, index + delta)));
  }

  /** Show the preceding media item. */
  previousImage() { this.#move(-1); }
  /** Show the next media item. */
  nextImage() { this.#move(1); }

  /** @param {MouseEvent} event - Framework-routed thumbnail click. */
  selectImage(event) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a');
    if (!(link instanceof HTMLAnchorElement) || !this.refs.thumbnails?.includes(link)) return;
    event.preventDefault();
    this.#showCurrent(this.refs.thumbnails.indexOf(link));
  }

  /** @param {number} [requestedIndex] - Explicit thumbnail or native fragment selection. */
  #showCurrent(requestedIndex) {
    const { images, thumbnails, slides } = this.refs;
    if (!images?.length || !thumbnails?.length || !slides) return;
    const index = Math.max(0, requestedIndex ?? thumbnails.findIndex((link) => link.getAttribute('aria-current') === 'true'));
    for (const [thumbnailIndex, thumbnail] of thumbnails.entries()) {
      if (thumbnailIndex === index) thumbnail.setAttribute('aria-current', 'true');
      else thumbnail.removeAttribute('aria-current');
    }
    for (const [imageIndex, image] of images.entries()) {
      const selected = imageIndex === index;
      if (selected) {
        const fallback = image.querySelector('noscript[data-gallery-image]');
        if (fallback) {
          // This is server-rendered media markup, inert until the customer selects it.
          const template = document.createElement('template');
          template.innerHTML = fallback.textContent || '';
          fallback.replaceWith(template.content);
        }
      }
      // Initial LCP is always visible; animate only a later user selection.
      image.toggleAttribute('data-selected', selected && this.#initialized && requestedIndex !== undefined);
      image.hidden = !selected;
      if (image.hidden && image.querySelector('video, deferred-media, product-model')) this.#pauseMedia(image);
    }
    slides.scrollLeft = 0;
    if (this.refs.previous) this.refs.previous.disabled = index === 0;
    if (this.refs.next) this.refs.next.disabled = index === images.length - 1;
    const thumbnail = thumbnails[index];
    // The initial selection is already in view. Read geometry only for navigation.
    if (requestedIndex !== undefined && thumbnail && this.refs.strip) {
      const item = thumbnail.getBoundingClientRect();
      const strip = this.refs.strip.getBoundingClientRect();
      if (item.left < strip.left || item.right > strip.right) this.refs.strip.scrollLeft += item.left - strip.left;
    }
  }
}

if (!customElements.get('evenmorn-gallery')) customElements.define('evenmorn-gallery', EvenmornGallery);
